const ENDPOINT = '/api/analytics.php';
const FLUSH_MS = 2000;
const MAX_BATCH = 20;
const MAX_ITEMS = 100;

/** @type {Array<Record<string, unknown>>} */
let queue = [];
/** @type {number|null} */
let flushTimer = null;

/**
 * @param {unknown} query
 * @returns {string|null}
 */
function normalizeQuery(query) {
  if (typeof query !== 'string') {
    return null;
  }
  const trimmed = query.trim().toLowerCase();
  if (trimmed === '') {
    return null;
  }
  return trimmed.length > 255 ? trimmed.slice(0, 255) : trimmed;
}

/**
 * @param {unknown} slug
 * @returns {string|null}
 */
function normalizeSlug(slug) {
  if (typeof slug !== 'string') {
    return null;
  }
  const trimmed = slug.trim().toLowerCase();
  if (trimmed === '') {
    return null;
  }
  return trimmed.length > 64 ? trimmed.slice(0, 64) : trimmed;
}

/**
 * @param {unknown} source
 * @returns {string}
 */
function normalizeSource(source) {
  const allowed = new Set([
    'search',
    'category',
    'landing',
    'browse',
    'sale',
    'homepage',
  ]);
  if (typeof source !== 'string') {
    return 'browse';
  }
  const trimmed = source.trim().toLowerCase();
  return allowed.has(trimmed) ? trimmed : 'browse';
}

/**
 * @param {unknown} page
 * @returns {number}
 */
function normalizePage(page) {
  const value = typeof page === 'number' ? page : Number(page);
  if (!Number.isFinite(value) || value < 1) {
    return 1;
  }
  return Math.min(255, Math.trunc(value));
}

/**
 * @param {unknown} position
 * @returns {number|null}
 */
function normalizePosition(position) {
  const value = typeof position === 'number' ? position : Number(position);
  if (!Number.isFinite(value) || value < 1) {
    return null;
  }
  return Math.min(65535, Math.trunc(value));
}

/**
 * @param {unknown} step
 * @returns {number|null}
 */
function normalizeSearchFallbackStep(step) {
  if (step === null || step === undefined || step === '') {
    return null;
  }
  const value = typeof step === 'number' ? step : Number(step);
  if (!Number.isFinite(value) || value < 0 || value > 255) {
    return null;
  }
  return Math.trunc(value);
}

/**
 * @param {unknown} items
 * @returns {number[]}
 */
function normalizeItemIds(items) {
  if (!Array.isArray(items)) {
    return [];
  }
  /** @type {number[]} */
  const out = [];
  for (const item of items) {
    const value = typeof item === 'number' ? item : Number(item);
    if (!Number.isFinite(value) || value <= 0) {
      continue;
    }
    out.push(Math.trunc(value));
    if (out.length >= MAX_ITEMS) {
      break;
    }
  }
  return out;
}

/**
 * @param {Record<string, unknown>} event
 */
function enqueue(event) {
  queue.push(event);
  if (queue.length >= MAX_BATCH) {
    flush();
    return;
  }
  scheduleFlush();
}

function scheduleFlush() {
  if (flushTimer !== null) {
    return;
  }
  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    flush();
  }, FLUSH_MS);
}

export function flushAnalytics() {
  flush();
}

function flush() {
  if (queue.length === 0) {
    return;
  }

  const events = queue.splice(0, MAX_BATCH);
  const body = JSON.stringify({ events });
  const blob = new Blob([body], { type: 'application/json' });

  if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    if (navigator.sendBeacon(ENDPOINT, blob)) {
      if (queue.length > 0) {
        scheduleFlush();
      }
      return;
    }
  }

  fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
  }).catch(() => {});

  if (queue.length > 0) {
    scheduleFlush();
  }
}

/**
 * Stable key for one SERP impression. Treats auto-initialized full-range
 * price bounds the same as unset filters so the post-fetch refetch does not
 * double-count.
 *
 * @param {{
 *   query?: string,
 *   productCategoryId?: string,
 *   websiteHost?: string,
 *   shopCategorySlug?: string,
 *   saleActive?: boolean,
 *   onlyInStock?: boolean,
 *   sortBy?: string,
 *   retailerFilterKey?: string,
 *   page?: number,
 *   filterPriceMin?: number|null,
 *   filterPriceMax?: number|null,
 *   domainMin?: number|null,
 *   domainMax?: number|null,
 *   searchFallbackStep?: number|null,
 * }} params
 * @returns {string}
 */
export function buildSerpResultsImpressionKey({
  query = '',
  productCategoryId = '',
  websiteHost = '',
  shopCategorySlug = '',
  saleActive = false,
  onlyInStock = false,
  sortBy = 'featured',
  retailerFilterKey = '',
  page = 1,
  filterPriceMin = null,
  filterPriceMax = null,
  domainMin = null,
  domainMax = null,
}) {
  let pricePart = '';
  if (
    filterPriceMin != null
    && filterPriceMax != null
    && domainMin != null
    && domainMax != null
    && (filterPriceMin > domainMin || filterPriceMax < domainMax)
  ) {
    pricePart = `${filterPriceMin}\0${filterPriceMax}`;
  }

  return [
    query,
    productCategoryId,
    websiteHost,
    shopCategorySlug,
    saleActive ? '1' : '0',
    onlyInStock ? '1' : '0',
    sortBy,
    retailerFilterKey,
    page,
    pricePart,
  ].join('\0');
}

/**
 * @param {{ current: string }} lastImpressionKeyRef
 * @param {{
 *   query?: string|null,
 *   shopCategorySlug?: string|null,
 *   source?: string,
 *   page?: number,
 *   items?: unknown[],
 *   scopeChange?: boolean,
 *   productCategoryId?: string,
 *   websiteHost?: string,
 *   saleActive?: boolean,
 *   onlyInStock?: boolean,
 *   sortBy?: string,
 *   retailerFilterKey?: string,
 *   filterPriceMin?: number|null,
 *   filterPriceMax?: number|null,
 *   domainMin?: number|null,
 *   domainMax?: number|null,
 *   searchFallbackStep?: number|null,
 * }} params
 */
export function trackSerpResults(lastImpressionKeyRef, params) {
  const impressionKey = buildSerpResultsImpressionKey({
    query: params.query ?? '',
    productCategoryId: params.productCategoryId ?? '',
    websiteHost: params.websiteHost ?? '',
    shopCategorySlug: params.shopCategorySlug ?? '',
    saleActive: params.saleActive ?? false,
    onlyInStock: params.onlyInStock ?? false,
    sortBy: params.sortBy ?? 'featured',
    retailerFilterKey: params.retailerFilterKey ?? '',
    page: params.page ?? 1,
    filterPriceMin: params.filterPriceMin ?? null,
    filterPriceMax: params.filterPriceMax ?? null,
    domainMin: params.domainMin ?? null,
    domainMax: params.domainMax ?? null,
  });

  if (lastImpressionKeyRef.current === impressionKey) {
    return;
  }
  lastImpressionKeyRef.current = impressionKey;

  trackResults({
    query: params.query,
    shopCategorySlug: params.shopCategorySlug,
    source: params.source,
    page: params.page,
    items: params.items,
    scopeChange: params.scopeChange,
    searchFallbackStep: params.searchFallbackStep,
  });
}

/**
 * @param {{
 *   query?: string|null,
 *   shopCategorySlug?: string|null,
 *   source?: string,
 *   page?: number,
 *   items?: unknown[],
 *   scopeChange?: boolean,
 *   searchFallbackStep?: number|null,
 * }} params
 */
export function trackResults({
  query,
  shopCategorySlug,
  source,
  page,
  items,
  scopeChange = true,
  searchFallbackStep = null,
}) {
  enqueue({
    type: 'results',
    query: normalizeQuery(query ?? null),
    shopCategorySlug: normalizeSlug(shopCategorySlug ?? null),
    source: normalizeSource(source),
    page: normalizePage(page),
    items: normalizeItemIds(items),
    scopeChange: scopeChange !== false,
    searchFallbackStep: normalizeSearchFallbackStep(searchFallbackStep),
  });
}

/**
 * @param {{
 *   listingProductId: string|number,
 *   query?: string|null,
 *   shopCategorySlug?: string|null,
 *   source?: string,
 *   page?: number|null,
 *   position?: number|null,
 *   searchFallbackStep?: number|null,
 * }} params
 */
export function trackClick({
  listingProductId,
  query,
  shopCategorySlug,
  source,
  page,
  position,
  searchFallbackStep = null,
}) {
  const id = typeof listingProductId === 'number'
    ? listingProductId
    : Number(listingProductId);
  if (!Number.isFinite(id) || id <= 0) {
    return;
  }

  const normalizedPage = page == null ? null : normalizePage(page);
  enqueue({
    type: 'click',
    listingProductId: Math.trunc(id),
    query: normalizeQuery(query ?? null),
    shopCategorySlug: normalizeSlug(shopCategorySlug ?? null),
    source: normalizeSource(source),
    page: normalizedPage,
    position: normalizePosition(position),
    searchFallbackStep: normalizeSearchFallbackStep(searchFallbackStep),
  });
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flush();
    }
  });
}
